// src/pages/orders/components/OrderFormFieldSettingsModal.jsx
import React, { useState, useEffect } from 'react';
import { X, SlidersHorizontal, Save, Loader2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { ORDER_FORM_FIELDS_NAMESPACE, CONFIGURABLE_ORDER_FIELDS } from '../../../utils/orderFormFields';
import styles from './OrderFormFieldSettingsModal.module.css';

const FIELD_LABEL_KEYS = {
  privateReference: 'ecomOrders.privateReference',
  customerEmail: 'ecomOrders.customerEmail',
  deliveryNotes: 'ecomOrders.deliveryNotes',
  specialNotes: 'ecomOrders.specialNotes',
  orderOriginKey: 'ecomOrders.orderOrigin',
};

const OrderFormFieldSettingsModal = ({ onClose }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { settings, isLoading, saveSettings } = useBusinessSettings(ORDER_FORM_FIELDS_NAMESPACE);

  const [enabled, setEnabled] = useState(false);
  const [requiredFields, setRequiredFields] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!settings?.enabled);
    setRequiredFields(settings?.requiredFields || {});
  }, [settings]);

  const handleFieldToggle = (field, checked) => {
    setRequiredFields((prev) => ({ ...prev, [field]: checked }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveSettings({ enabled, requiredFields });
      success(t('ecomOrders.orderFieldSettingsSaved'));
      onClose();
    } catch (err) {
      showError(err.response?.data?.message || t('ecomOrders.orderFieldSettingsSaveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerTitle}>
            <SlidersHorizontal size={20} />
            <h2>{t('ecomOrders.orderFieldSettings')}</h2>
          </div>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.description}>{t('ecomOrders.orderFieldSettingsDescription')}</p>

          <div className={styles.toggleRow}>
            <div>
              <div className={styles.fieldLabel}>{t('ecomOrders.enableFieldRequirements')}</div>
              <div className={styles.fieldHelp}>{t('ecomOrders.enableFieldRequirementsHelp')}</div>
            </div>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={enabled}
                disabled={isLoading || isSaving}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className={styles.toggleSlider} />
            </label>
          </div>

          <div className={`${styles.fieldList} ${!enabled ? styles.fieldListDisabled : ''}`}>
            {CONFIGURABLE_ORDER_FIELDS.map((field) => (
              <div key={field} className={styles.toggleRow}>
                <div>
                  <div className={styles.fieldLabel}>{t(FIELD_LABEL_KEYS[field])}</div>
                  <div className={styles.fieldHelp}>
                    {requiredFields[field] ? t('ecomOrders.fieldRequired') : t('ecomOrders.fieldOptional')}
                  </div>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={!!requiredFields[field]}
                    disabled={!enabled || isLoading || isSaving}
                    onChange={(e) => handleFieldToggle(field, e.target.checked)}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelButton} disabled={isSaving}>
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} className={styles.saveButton} disabled={isLoading || isSaving}>
            {isSaving ? <Loader2 size={16} className={styles.spinning} /> : <Save size={16} />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderFormFieldSettingsModal;
